import { AssetKey } from '@constants/asset-keys.const';
import Phaser from 'phaser';
import { NodeVisualState } from '@enums/node-states.enum';
import { NodeLayoutItem } from '@models/layout';
import { NodeConfig, NodeView } from '@models/nodes';
import { DPR, NODE_DEPTH } from '@constants/common.const';
import {
  createBubblesSprite,
  playAnimation,
} from '../../../ui/animations/BubblesFx';
import { Tooltip } from '../../../ui/Tooltip';
import { BaseNodeStrategy } from './BaseNodeStrategy';
import {
  NodeImageKey,
  ExtrasResult,
  NodeEventContext,
  StateChangeHelpers,
} from './NodeVisualStrategy';

/**
 * Strategy for Reward node type.
 * Uses node-action-completed/node-action-upcoming textures and creates reward icons with shine + bounce animation.
 */
export class RewardNodeStrategy extends BaseNodeStrategy {
  // Cache FX sprites per scene to avoid recreating them
  private fxCache = new WeakMap<Phaser.Scene, Phaser.GameObjects.Sprite>();

  getDefaultTexture(_scene: Phaser.Scene): NodeImageKey {
    return AssetKey.NodeActionUpcoming;
  }

  getTextureForState(
    state: NodeVisualState,
    _scene: Phaser.Scene,
  ): NodeImageKey {
    if (state === NodeVisualState.Default) {
      return AssetKey.NodeActionUpcoming;
    }
    if (state === NodeVisualState.Passed || state === NodeVisualState.Active) {
      return AssetKey.NodeActionCompleted;
    }
    return AssetKey.NodeActionUpcoming;
  }

  createExtras(
    scene: Phaser.Scene,
    node: NodeLayoutItem,
    config: NodeConfig,
  ): ExtrasResult | null {
    // key is action UUID
    const rewardKey = node.action?.uuid;

    if (!rewardKey || !scene.textures.exists(rewardKey)) return null;

    const offsetY = config.height * 0.3;
    const rewardIcon = scene.add.image(node.x, node.y - offsetY, rewardKey);
    const size = 40 * DPR;

    rewardIcon
      .setDisplaySize(size, size)
      .setOrigin(0.5)
      .setDepth(NODE_DEPTH + 10)
      .setAlpha(0)
      .setScrollFactor(1);

    rewardIcon.postFX.addShine(0.4, 0.2, 5);

    this.playRewardAppear(scene, rewardIcon, node.y - offsetY);

    return { rewardIcon };
  }

  onFlashExtra(
    scene: Phaser.Scene,
    view: NodeView,
    message: string,
  ): Promise<void> {
    return new Promise<void>((resolve) => {
      const { rewardIcon: reward } = view;
      if (!reward) {
        resolve();
        return;
      }
      // remove black and white filter
      reward.preFX?.clear();

      // Stop idle bounce animation
      scene.tweens.killTweensOf(reward);

      const startY = reward.y;
      const bounceHeight = 30 * DPR;

      // Bounce up with rotation wiggle
      scene.tweens.add({
        targets: reward,
        y: startY - bounceHeight,
        duration: 300,
        ease: 'Back.Out',
        onComplete: () => {
          // Wiggle rotation side to side
          scene.tweens.add({
            targets: reward,
            angle: { from: -15, to: 15 },
            duration: 100,
            yoyo: true,
            repeat: 5,
            ease: 'Sine.easeInOut',
            onComplete: () => {
              // Return to the original position
              scene.tweens.add({
                targets: reward,
                y: startY,
                angle: 0,
                duration: 300,
                ease: 'Back.In',
                onComplete: () => {
                  const tooltip = new Tooltip({
                    scene: scene,
                    text: message,
                    x: reward.x,
                    y: reward.y - reward.displayHeight / 2 - 20 * DPR,
                    autoHideDelayMs: 2000,
                    fadeOutDurationMs: 150,
                    maxWidth: 150,
                    onHide: resolve,
                  });

                  tooltip.show();
                },
              });
            },
          });
        },
      });
    });
  }

  onStateChange(
    view: NodeView,
    state: NodeVisualState,
    helpers: StateChangeHelpers,
  ): void {
    super.onStateChange(view, state, helpers);
    // On restore (e.g. page reload), passed reward icons must be greyed out
    // to match the live onPassThrough visual. Without this they stay colorful.
    if (state === NodeVisualState.Passed) {
      this.greyOutRewardIcon(view);
    }
  }

  /**
   * Called when player passes through without landing - mark reward as missed.
   */
  onPassThrough(_context: NodeEventContext, view: NodeView): void {
    this.greyOutRewardIcon(view);
  }

  private greyOutRewardIcon(view: NodeView): void {
    const reward = view.rewardIcon;
    if (!reward) return;
    // Clear postFX (shine effect) first, otherwise it masks the grayscale
    reward.postFX?.clear();
    const fx = reward.preFX?.addColorMatrix();
    if (fx) fx.blackWhite(true);
  }

  onLanded(context: NodeEventContext, view: NodeView): void {
    const { scene } = context;
    const reward = view.rewardIcon;
    if (!reward) return;
    scene.tweens.killTweensOf(reward);
  }

  onRewardConfirmed(context: NodeEventContext, view: NodeView): void {
    const { scene } = context;
    const reward = view.rewardIcon;
    if (!reward) return;

    scene.tweens.killTweensOf(reward);

    const startY = reward.y;
    const bounceHeight = 30 * DPR;

    scene.tweens.add({
      targets: reward,
      y: startY - bounceHeight,
      duration: 300,
      ease: 'Back.Out',
      onComplete: () => {
        scene.tweens.add({
          targets: reward,
          angle: { from: -15, to: 15 },
          duration: 100,
          yoyo: true,
          repeat: 5,
          ease: 'Sine.easeInOut',
          onComplete: () => {
            scene.tweens.add({
              targets: reward,
              y: startY,
              angle: 0,
              duration: 300,
              ease: 'Back.In',
              onComplete: () => {
                this.playBubblesAnimation(context, view);
              },
            });
          },
        });
      },
    });
  }

  private playRewardAppear(
    scene: Phaser.Scene,
    rewardIcon: Phaser.GameObjects.Image,
    targetY: number,
  ): void {
    const startY = targetY - 80 * DPR;
    rewardIcon.setY(startY);

    scene.tweens.add({
      targets: rewardIcon,
      y: targetY,
      alpha: 1,
      duration: 500,
      ease: 'Back.Out',
      onComplete: () => this.startRewardBounce(scene, rewardIcon, targetY),
    });
  }

  private startRewardBounce(
    scene: Phaser.Scene,
    rewardIcon: Phaser.GameObjects.Image,
    targetY: number,
  ): void {
    scene.tweens.add({
      targets: rewardIcon,
      y: targetY - 3 * DPR,
      duration: 700,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private playBubblesAnimation(
    context: NodeEventContext,
    view: NodeView,
  ): void {
    const { scene, fxLayer } = context;

    // Lazily create and cache the FX sprite
    let fx = this.fxCache.get(scene);
    if (fx && !fx.active) {
      this.fxCache.delete(scene);
      fx = undefined;
    }
    if (!fx) {
      fx =
        createBubblesSprite(scene, {
          x: 0,
          y: 0,
          depth: 0,
          scrollFactor: 1,
        }) ?? undefined;

      if (fx) {
        fx.setVisible(false);
        fxLayer.add(fx);
        this.fxCache.set(scene, fx);
      }
    }

    if (!fx) return;

    // Place the FX at the checkpoint node's top-center
    const bounds = view.sprite.getBounds();
    const parent = view.sprite.parentContainer;
    const localX = parent ? bounds.centerX - parent.x : bounds.centerX;
    const localY = parent ? bounds.top - parent.y : bounds.top;

    fx.setPosition(localX, localY);
    fx.setVisible(true);

    // Restart if it was already playing
    fx.anims?.stop();

    playAnimation(scene, fx, {
      repeat: 0,
    });

    fx.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
      if (!fx.active) return;
      fx.setVisible(false);
    });
  }
}
